/**
 * 브랜드 명칭·법인 정보 SSoT.
 * 셸(헤더·푸터)·문서 타이틀 등 하드코딩 문구는 전부 이 설정을 참조한다.
 * 출처: design-prototypes (고객 합의 최종안). 명칭/정보 변경 시 여기만 수정한다.
 */
export const brand = {
  /**
   * 사이트 정식 origin — canonical·OG url·sitemap `<loc>`·JSON-LD 절대 URL 의 단일 기준.
   *
   * 검색엔진은 http/https·www 유무를 별개 URL 로 취급하므로, 여기 값 하나로 정식 주소를 고정한다
   * (vite.config.ts 의 sitemap 생성도 이 상수를 import 해 프리렌더 canonical 과 1:1 로 맞춘다).
   * 도메인 이전 시 여기만 수정한다 — 절대 URL 을 페이지·데이터에 재하드코딩하지 않는다.
   */
  origin: 'https://myipsi.co.kr',
  /** 서비스 워드마크 */
  name: '대학 합격의 길',
  /** 발행 교육그룹 */
  group: '데오럭스 교육그룹',
  /** 문서 타이틀·메타 설명용 한 줄 */
  tagline: '대학별 입시 전략 리포트',
  /** 셸 로고 이미지(2종 + 구분선) — public/assets 경로 */
  logos: {
    daehak: { src: '/assets/daehak-logo.png', alt: '대학 합격의 길' },
    deolux: { src: '/assets/deolux-logo.png', alt: '데오럭스 교육그룹' },
  },
  /** 사업자 정보(푸터) */
  legal: {
    company: '데오럭스 주식회사, 도서출판 데오럭스',
    ceo: '장광원',
    bizNo: '417-81-51332',
    mailOrderNo: '2026-서울강남-03364',
    publisherNo: '제2024-153호',
    address: '서울특별시 강남구 삼성로71길 14, 5층',
    tel: '02-3426-8905',
    email: 'deoluxbooks@naver.com',
    copyright: 'Copyright © 2026 대학 합격의 길. All rights reserved.',
  },
  /** 외부 상담 문의 링크 */
  consultUrl: 'https://deolux.or.kr/문의하기/',
  /**
   * 카카오톡 채널 1:1 채팅 URL — 랜딩 플로팅 문의 버튼의 목적지.
   * 채널 개설 후 `http://pf.kakao.com/_채널ID/chat` 로 채운다.
   * 빈 문자열이면 플로팅 버튼이 렌더링되지 않는다(채널 미개설 상태 안전장치).
   */
  kakaoChannelChatUrl: 'https://pf.kakao.com/_xeZTBX/chat',
  /**
   * 네이버 블로그 URL — 푸터 브랜드 열의 블로그 아이콘 링크 목적지.
   * 빈 문자열이면 아이콘이 렌더링되지 않는다(운영 중단 시 안전장치).
   * 블로그가 입시 컨설팅 유입 채널이라 PG(토스) 연동 심사에 우회결제 유도로
   * 비칠 수 있어 심사 통과 전까지 비활성 — 통과 후 아래 주석 URL 로 교체한다.
   */
  naverBlogUrl: '', // 'https://blog.naver.com/deolux311'
  /**
   * FO 두 축(커머스 ↔ AI 입시분석) 교차 진입 링크 SSoT.
   *
   * MVP 는 같은 오리진 Path 구분(`/` ↔ `/analysis`) — 서브도메인 분리는 보류(장래 옵션).
   * 두 축이 서브도메인으로 갈릴 때 이 값만 절대 URL(`https://analysis...` / `https://www...`)로
   * 교체하면 헤더 진입 버튼이 자동 이관된다 — 양쪽 헤더에 목적지를 흩뿌리지 않기 위한 단일 지점.
   */
  crossLinks: {
    /** 커머스(자료실·리포트) 진입 — 워크스페이스 헤더에서 복귀용. */
    commerce: '/',
    /** AI 입시분석 워크스페이스 진입 — 커머스 헤더에서 진입용. */
    analysis: '/analysis',
  },
} as const;

export type Brand = typeof brand;

/**
 * 사이트 내부 경로를 **정식(canonical) 절대 URL** 로 만든다 — canonical·OG url·sitemap `<loc>`·
 * JSON-LD `item`/`url` 등 검색엔진에 노출되는 모든 절대 URL 의 단일 생성 지점.
 *
 * ## 왜 trailing slash 인가
 *
 * 프리렌더는 `dirStyle: 'nested'`(vite.config.ts)라 `/guide/yonsei` 가
 * `dist/guide/yonsei/index.html` 로 생성되고, Nginx `try_files $uri $uri/` 가 이를 **디렉토리**로
 * 매칭한다. nginx 는 디렉토리 서빙 시 **반드시 슬래시 URL 로 301 한 뒤** index 를 낸다 — 이는
 * nginx 내장 동작이라 끌 수 없다(`absolute_redirect off` 는 Location 을 상대경로로 바꿀 뿐
 * 301 자체를 없애지 못한다 — 과거 이 지시어를 오해해 슬래시 없는 canonical 을 발행했다).
 *
 * 그 결과 슬래시 없는 URL 을 canonical 로 선언하면 **자기 자신이 리다이렉트 대상**이 되어,
 * 구글이 사용자 선언 canonical 을 버리고 자체 판단(슬래시 붙은 형태)을 쓴다
 * (2026-08 GSC "리디렉션이 포함된 페이지" 12건 = sitemap 전 URL). 따라서 실제 200 을 내는
 * 형태(슬래시 있음)를 정규형으로 확정하고, 절대 URL 은 전부 이 함수를 경유시킨다.
 *
 * @param path 선행 슬래시로 시작하는 내부 경로(예: `/guide/yonsei`). 쿼리·해시는 넣지 않는다.
 * @returns 정규 절대 URL(항상 `/` 로 끝남) — 루트는 `https://origin/`.
 */
export function canonicalUrl(path: string): string {
  return `${brand.origin}${canonicalPath(path)}`;
}

/**
 * 내부 경로를 정규형(항상 trailing slash)으로 맞춘다 — 절대 URL 이 아닌 `<a href>`·sitemap 경로용.
 * 근거는 {@link canonicalUrl} 참조.
 */
export function canonicalPath(path: string): string {
  return path.endsWith('/') ? path : `${path}/`;
}
